import React from 'react';
import {selectCmsItemsIgnoringLocalization} from '@packages/systems/cms/client-selectors';
import {getItemsForCollection} from '@packages/shared/utils/ItemUtils';
import {DeprecatedPopoverBridge} from '@packages/systems/design-system/PopoverBridge';
import * as FinderInput from '@packages/systems/design-system/FinderInput';
import useFuzzySearch from '@packages/systems/ui/hooks/useFuzzySearch';
import * as Menu from '@packages/systems/design-system/Menu';
import {
  styled,
  useTheme,
  emotionStyled,
  tokens,
} from '@packages/utilities/styled';
import {chainFunctions} from '@packages/utilities/component';
import {FinePrint} from '@packages/systems/design-system/Text';
import {PropertyListNone} from '@packages/systems/ui/shared-ui/PropertyList';
import {useSharedFlowContext} from '@packages/systems/flows/shared';
import useDebouncedValue from '@packages/systems/ui/hooks/useDebouncedValue';
import {truncate} from '@packages/utilities/string-utils';
import {VisuallyHidden} from '@packages/systems/spring';
import {useDesignerSelector} from '@packages/systems/designer/flux';

const selectCollections = (state: any) => state.CollectionStore.collections;
const getSearchableText = (item: any) => item.name;

type itemSelectType = (item?: any) => any;

/**
 * The search input and popover for items in the Delete and Update blocks.
 */
export const ItemSearchInputPopover = React.memo<{
  onItemSelect: itemSelectType;
}>(function ItemSearchInputPopover({onItemSelect}) {
  const {useCMSBlock} = useSharedFlowContext();
  // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
  const {collectionName} = useCMSBlock();
  const collectionRecords = useDesignerSelector(selectCollections);
  // eslint-disable-next-line no-restricted-syntax -- TODO: Please reuse an existing selector here or define a new one
  const itemsFromStore = useDesignerSelector((state) =>
    selectCmsItemsIgnoringLocalization(state.CollectionStore)
  );
  const {control, colors} = useTheme();

  const [searchText, setSearchText] = React.useState('');
  const [preselected, setPreselected] = React.useState<any>(null);

  const selectedCollection = collectionRecords.find(
    // @ts-expect-error - TS7006 - Parameter 'coll' implicitly has an 'any' type.
    (coll) => coll.get('name', '') === collectionName
  );
  const selectedCollectionId =
    selectedCollection && selectedCollection.get('_id', '');

  const onSelectChange = React.useCallback(
    // @ts-expect-error - TS7006 - Parameter 'item' implicitly has an 'any' type.
    (item) => {
      onItemSelect(item);
    },
    [onItemSelect]
  );
  const items: {id: string; name: string}[] = React.useMemo(
    () =>
      getItemsForCollection(itemsFromStore, selectedCollectionId)
        .map((item) => ({
          id: item.get('_id', ''),
          name: item.get('name', ''),
        }))
        .toArray(),
    [itemsFromStore, selectedCollectionId]
  );

  const [matches, {focus}, {onChange, onKeyDown, onFocus, onBlur, clear}] =
    useFuzzySearch({
      query: searchText,
      onQueryChange: setSearchText,
      items,
      getSearchableText,
      onSelectChange,
      onPreselectChange: setPreselected,
    });

  const clearSearch = () => {
    clear();
    setPreselected(null);
  };

  // only show the 1st 10 so the dropdown doesn't get too big.
  const subset = matches.slice(0, 10);

  return (
    <DeprecatedPopoverBridge
      borderWidth={control.borderWidth}
      borderColor={colors.logicFlows.blockBorderColor}
      showMask="TRANSPARENT"
      showArrow={false}
      metricsIdentifier="item-search-input-popover"
      renderPopoverContent={({closePopover}) => (
        <MenuWrapper>
          <>
            {matches.length === 0 && items.length > 0 && (
              <NoMatchesWrapper>
                <StyledPropertyListNone label="No matching items" />
              </NoMatchesWrapper>
            )}

            {items.length === 0 && (
              <NoItems selectedCollection={selectedCollection} />
            )}

            {matches.length > 0 && (
              <Menu.Wrapper>
                {subset.map(({match, actions}, i) => (
                  <StyledMenuItem
                    first={i === 0}
                    last={i === matches.length - 1}
                    data-automation-id={`item-match-${i}`}
                    key={`item-match-${match.id}-${i}`}
                    preselected={preselected === match}
                    // @ts-expect-error - error TS2322 - Type '(...args: unknown[]) => void' is not assignable to type '(e: MouseEvent<HTMLElement, MouseEvent>) => Promise<any> | undefined'.
                    onClick={chainFunctions(
                      actions.select,
                      clearSearch,
                      closePopover
                    )}
                    onMouseEnter={actions.preselect}
                  >
                    <span>
                      <StyledFinePrint
                        preselected={preselected === match}
                      >{`${truncate(match.name, 36)}`}</StyledFinePrint>{' '}
                    </span>
                  </StyledMenuItem>
                ))}
              </Menu.Wrapper>
            )}
          </>
        </MenuWrapper>
      )}
    >
      {({Anchor, openPopover}) => (
        <Anchor asChild>
          <div>
            <SearchWrapper>
              <SearchIcon focus={focus} />
              <SearchInput
                searchText={searchText}
                // @ts-expect-error - TS2322 - Type '{ searchText: string; clearSearch: () => void; focus: boolean; onFocus: () => void; openPopover: (arg1: unknown) => void; onBlur: () => void; onChange: (event: ChangeEvent<EventTarget>) => void; onKeyDown: (event: KeyboardEvent<...>) => void; selectedCollection: any; }' is not assignable to type 'IntrinsicAttributes & { searchText: any; focus: any; onFocus: any; openPopover: any; onBlur: any; onChange: any; onKeyDown: any; selectedCollection: any; }'.
                clearSearch={clearSearch}
                focus={focus}
                onFocus={onFocus}
                openPopover={openPopover}
                onBlur={onBlur}
                onChange={onChange}
                onKeyDown={onKeyDown}
                selectedCollection={selectedCollection}
              />
              {searchText && <SearchCloseIconButton onClick={clearSearch} />}
            </SearchWrapper>
            <VisuallyHidden id="item-search-description">
              Search for an item by name or slug.
            </VisuallyHidden>
          </div>
        </Anchor>
      )}
    </DeprecatedPopoverBridge>
  );
});

// @ts-expect-error - TS7031 - Binding element 'selectedCollection' implicitly has an 'any' type.
const NoItems = ({selectedCollection}) => {
  const {useCollectionItemsPage} = useSharedFlowContext();
  // In case the store has no items yet, load the first 10 items from the DB.
  // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
  useCollectionItemsPage(selectedCollection, {format: 'itemRef', limit: 10});

  return (
    <NoMatchesWrapper>
      <StyledPropertyListNone label="No items in collection" />
    </NoMatchesWrapper>
  );
};

const DEFAULT_DEBOUNCED_WAIT = 200;

const SearchInput = ({
  // @ts-expect-error - TS7031 - Binding element 'searchText' implicitly has an 'any' type.
  searchText,
  // @ts-expect-error - TS7031 - Binding element 'focus' implicitly has an 'any' type.
  focus,
  // @ts-expect-error - TS7031 - Binding element 'onFocus' implicitly has an 'any' type.
  onFocus,
  // @ts-expect-error - TS7031 - Binding element 'openPopover' implicitly has an 'any' type.
  openPopover,
  // @ts-expect-error - TS7031 - Binding element 'onBlur' implicitly has an 'any' type.
  onBlur,
  // @ts-expect-error - TS7031 - Binding element 'onChange' implicitly has an 'any' type.
  onChange,
  // @ts-expect-error - TS7031 - Binding element 'onKeyDown' implicitly has an 'any' type.
  onKeyDown,
  // @ts-expect-error - TS7031 - Binding element 'selectedCollection' implicitly has an 'any' type.
  selectedCollection,
}) => {
  const {useCollectionItemsPage} = useSharedFlowContext();
  const debouncedQuery = useDebouncedValue({
    value: searchText,
    wait: DEFAULT_DEBOUNCED_WAIT,
  });
  // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
  useCollectionItemsPage(selectedCollection, {
    format: 'itemRef',
    query: debouncedQuery,
  });
  return (
    <FinderInput.Input
      // HACK: to get around losing input focus when popover opens
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={focus}
      onFocus={chainFunctions(onFocus, openPopover)}
      onBlur={onBlur}
      focus={focus}
      value={searchText}
      onChange={onChange}
      onKeyDown={onKeyDown}
      placeholder="Search items..."
      aria-describedby="item-search-description"
    />
  );
};

const SearchWrapper = styled(FinderInput.Wrapper)(({theme: {gutters}}) => ({
  background: undefined,
  padding: 0,
  marginBottom: gutters.x4,
  borderTop: 0,
  borderBottom: 0,
}));

const SearchIcon = styled(FinderInput.SearchIcon)(
  ({theme: {gutters, colors}}) => ({
    left: gutters.x8,
    color: colors.foregroundDim,
  }),
  {
    focus: ({theme: {colors}}) => ({
      color: colors.foreground,
    }),
  }
);

const SearchCloseIconButton = emotionStyled(FinderInput.CloseIconButton)({
  '&': {
    right: '0px',
  },
});

const StyledMenuItem = styled(Menu.Item)(
  ({theme: {fontSizes, gutters, control}, first, last}: any) => ({
    position: 'relative',
    width: `calc(100% - 2px)`,
    left: 1,
    marginTop: first ? gutters.x4 : undefined,
    marginBottom: last ? gutters.x4 : undefined,
    paddingTop: gutters.x4,
    paddingBottom: gutters.x4,
    fontSize: fontSizes.copy,
    height: control.height,
    display: 'flex',
    alignItems: 'center',
  }),
  {
    preselected: ({theme: {colors}}) => ({
      background: colors.backgroundVividest,
    }),
    disabled: ({theme: {colors}}) => ({
      color: colors.foregroundDim,
    }),
  }
);

const StyledFinePrint = styled(FinePrint)(
  ({theme: {colors}}: any) => ({
    display: 'inline',
    color: colors.foreground,
  }),
  {
    preselected: ({theme: {colors}}: any) => ({
      color: colors.white,
    }),
  }
);

const MenuWrapper = emotionStyled('div')({
  width: '240px',
  position: 'relative',
});

const NoMatchesWrapper = emotionStyled('div')({
  padding: tokens.space[2],
});

const StyledPropertyListNone = styled(PropertyListNone)(
  ({theme: {colors, control}}) => ({
    border: `${control.borderWidth}px solid ${colors.border}`,
    borderRadius: control.borderRadius,
  })
);
